import { useEffect, useState } from 'react';
import { useMetricType } from 'hooks/useMetricType';
import * as S from '../../PropertyPanel.styles';
import { IconButton, Tooltip } from '@mui/material';
import { PsdTableHead } from './PsdTableHead';
import { PsdTableAddDataRow } from './PsdTableAddDataRow';
import { PsdTableRow } from './PsdTableRow';
import { PsdTableRowEdit } from './PsdTableRowEdit';
import { StringInput } from 'components/StringInput';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Dropdown } from 'components/Dropdown';
import type { DisplayOption } from 'unitData/options/optionResolution';
import theme from 'theme/theme';
import {
  convertToCurrentMetric,
  convertToDefaultMetric,
} from 'util/conversions';

import { PsdGrid } from 'containers/PropertyPanel/Cards/Feed/PsdGrid';

enum CUSTOM_MATERIAL {
  CUSTOM = 'Custom',
}

export interface UpdatePsdInputInterface {
  name: string;
  percent?: number[];
  size: number[];
  maxStoneSize?: number;
}

/**
 * Interface for the PSD table
 */
export interface PsdTableInterface {
  psdName: string;
  psdValue?: string;
  maxStoneSize?: number;
  percent: number[];
  size: number[];
  curveEditIndex: number | undefined;
  updatePsd({
    name,
    percent,
    size,
    maxStoneSize,
  }: UpdatePsdInputInterface): void;
  materialOptions: (string | DisplayOption)[];
  hidePsd: boolean;
  toggleShowCardColumn?: () => void;
  showCardColumn?: boolean;
  enableFocusedCell?: boolean;
  hideOtherName?: boolean;
  otherPsdName?: string | undefined;
  setOtherPsdName?: (val: string | undefined) => void;
  changePsd: (selected: string) => void;
  conversionType?: string | undefined;
}

export const PsdTable = ({
  percent,
  size,
  curveEditIndex,
  psdName,
  psdValue,
  maxStoneSize,
  updatePsd,
  materialOptions,
  hidePsd,
  toggleShowCardColumn,
  showCardColumn,
  hideOtherName,
  otherPsdName,
  setOtherPsdName,
  changePsd,
  conversionType,
}: PsdTableInterface) => {
  const metricType = useMetricType();

  const [newPercentList, setNewPercentList] = useState<number[]>(percent);
  const [newSizeList, setNewSizeList] = useState<number[]>(size);
  const [selectedCell, setSelectedCell] = useState<boolean[]>([]);
  const [animateSave, setAnimateSave] = useState(-1);

  /* Update values if psd changed elsewhere (psd curve) */
  useEffect(() => {
    setNewPercentList(percent);
    setNewSizeList(size);
  }, [percent, size, psdName]);

  const [addNewRow, setAddNewRow] = useState(false);
  const [editIndex, setEditIndex] = useState<number>(-1);
  const [removeIndexes, setRemoveIndexes] = useState<number[]>([]);
  const closeAddRows = () => {
    setRemoveIndexes([]);
    setAddNewRow(false);
  };
  const closeEditRow = () => {
    setEditIndex(-1);
  };

  return (
    <>
      <S.FormContainer>
        <Dropdown
          value={psdValue ?? psdName}
          setValue={(val: string) => {
            changePsd(val);
          }}
          label={`Material Curve`}
          customDropDownOption={CUSTOM_MATERIAL.CUSTOM}
          dropDownOptions={materialOptions}
          marginRight={16}
          size={270}
        />
        {toggleShowCardColumn && (
          <S.IconContainer>
            <IconButton
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                toggleShowCardColumn?.();
              }}
              style={{ width: '100%' }}
            >
              <Tooltip title={'Flow curve'}>
                <ShowChartIcon
                  style={{
                    fill: showCardColumn
                      ? theme.palette.info.main
                      : theme.palette.grey[800],
                  }}
                />
              </Tooltip>
            </IconButton>
          </S.IconContainer>
        )}
      </S.FormContainer>
      {(psdValue ?? psdName) === CUSTOM_MATERIAL.CUSTOM && !hideOtherName && (
        <StringInput
          value={otherPsdName}
          setValue={(val) => {
            setOtherPsdName?.(val?.length > 0 ? val : undefined);
          }}
          label={'Custom name'}
        />
      )}

      <PsdGrid>
        <PsdTableHead
          removeIndexes={removeIndexes}
          setRemoveIndexes={(newRemoveIndexes: number[]) =>
            setRemoveIndexes(newRemoveIndexes)
          }
          editing={editIndex !== -1}
          addNewRow={addNewRow}
          toggleAddNewRow={() => setAddNewRow(!addNewRow)}
          newSizeList={newSizeList}
          remove={() => {
            const editedPercentList = [...newPercentList];
            const editedSizeList = [...newSizeList];
            const sortedRemoveIndexes = removeIndexes.sort((a, b) => a - b);
            for (let i = removeIndexes.length - 1; i >= 0; i--) {
              editedPercentList.splice(sortedRemoveIndexes[i], 1);
              editedSizeList.splice(sortedRemoveIndexes[i], 1);
            }
            setNewPercentList(editedPercentList);
            setNewSizeList(editedSizeList);
            updatePsd({
              name: psdName,
              percent: editedPercentList,
              size: editedSizeList,
              maxStoneSize: maxStoneSize,
            });
            setRemoveIndexes([]);
          }}
          hidePsd={hidePsd}
        />

        {addNewRow && (
          <PsdTableAddDataRow
            newSizeList={newSizeList}
            newPercentList={newPercentList}
            add={(addSizeList, addPercentList, addIndex) => {
              setNewPercentList(addPercentList);
              setNewSizeList(addSizeList);
              updatePsd({
                name: psdName,
                percent: addPercentList,
                size: addSizeList,
                maxStoneSize: maxStoneSize,
              });
              setAnimateSave(addIndex);
              setRemoveIndexes([]);
            }}
            hidePsd={hidePsd}
            close={closeAddRows}
            conversionType={conversionType}
          />
        )}

        {newPercentList.map((percentValue, index) => {
          const sizeValue = convertToCurrentMetric({
            value: newSizeList[index],
            metricType,
            conversionType: conversionType,
            decimals: 3,
          });
          const selected = removeIndexes?.includes(index);
          const editByCurve = curveEditIndex === index;
          const edit = editIndex === index;

          if (!edit) {
            return (
              <PsdTableRow
                cellTabbing={{
                  selectedCell: selectedCell,
                  setSelectedCell: setSelectedCell,
                }}
                key={`psd-row-${index}`}
                percentValue={percentValue}
                sizeValue={sizeValue}
                index={index}
                selected={selected}
                editByCurve={editByCurve}
                removeIndexes={removeIndexes}
                setRemoveIndexes={(newRemoveIndexes: number[]) =>
                  setRemoveIndexes(newRemoveIndexes)
                }
                setEditIndex={setEditIndex}
                animateSave={animateSave}
                hidePsd={hidePsd}
              />
            );
          } else {
            return (
              <PsdTableRowEdit
                cellTabbing={{
                  selectedCell: selectedCell,
                  setSelectedCell: setSelectedCell,
                }}
                key={`psd-row-${index}-edit`}
                percentValue={percentValue}
                sizeValue={sizeValue}
                newSizeList={newSizeList}
                newPercentList={newPercentList}
                index={index}
                setEditIndex={setEditIndex}
                save={(saveIndex, editPercent, editSize) => {
                  const editedPercentList = [...newPercentList];
                  const editedSizeList = [...newSizeList];
                  const sizeValue = convertToDefaultMetric({
                    value: editSize,
                    metricType,
                    conversionType: conversionType,
                  });
                  if (typeof sizeValue === 'number') {
                    editedPercentList[saveIndex] = editPercent;
                    editedSizeList[saveIndex] = sizeValue;
                    setNewPercentList(editedPercentList);
                    setNewSizeList(editedSizeList);
                    updatePsd({
                      name: psdName,
                      percent: editedPercentList,
                      size: editedSizeList,
                      maxStoneSize: maxStoneSize,
                    });
                    setAnimateSave(saveIndex);
                  }
                }}
                hidePsd={hidePsd}
                close={closeEditRow}
              />
            );
          }
        })}
      </PsdGrid>
    </>
  );
};
